import dayjs from 'dayjs';
import { useAppointmentStore } from '#fysio/app/stores/appointmentStore';
import type { AvailableDayType } from '#fysio/core/types/appointment-planner/AvailableDayType';
import type { BookAppointmentDataType } from '#fysio/core/types/appointment-planner/BookAppointmentDataType';
import type { NullablePatientDataType, PatientDataType } from '#fysio/core/types/appointment-planner/PatientDataType';
import type { TherapistType } from '#fysio/core/types/appointment-planner/TherapistType';
import type { TimeBlockType } from '#fysio/core/types/appointment-planner/TimeBlockType';
import type { RemainingSlotsType } from '#fysio/core/types/appointment-planner/RemainingSlotsType';

const loadingTherapists = ref<boolean>(false);

const loadingAvailableDays = ref<boolean>(false);
const loadingRemainingSlots = ref<boolean>(false);
const remainingSlots = ref<RemainingSlotsType>({ today: 0, tomorrow: 0, thisWeek: 0, nextWeek: 0, inTwoWeeks: 0 } as RemainingSlotsType);
const loadingAvailableTimeBlocks = ref<boolean>(false);
const availableTimeBlocks = ref<TimeBlockType[]>();

export const useAppointmentPlanner = () => {
	const store = useAppointmentStore();
	const notifications = useNotifications();
	const {
		trackBookTherapySession,
	} = useEventTracking();

	const selectTherapist = (therapist: TherapistType) => {
		store.setSelectedTherapist(therapist);
	};

	const selectDay = (day: AvailableDayType) => {
		store.setSelectedDay(day);
	};

	const selectTimeBlock = (timeBlock: TimeBlockType) => {
		store.setSelectedTimeBlock(timeBlock);
	};

	const fetchTherapists = async () => {
		if (store.therapists.length > 0) {
			return;
		}

		loadingTherapists.value = true;

		const {
			data,
		} = await useFetch('/api/appointment-planner/v1/therapists', {
			method: 'GET',
		});

		store.setTherapists(data.value ?? []);

		loadingTherapists.value = false;
	};

	const fetchAvailableDays = async () => {
		if (!store.selectedTherapist) {
			store.setAvailableDays([]);
			return;
		}

		loadingAvailableDays.value = true;

		const {
			data,
		} = await useFetch('/api/appointment-planner/v1/available-days', {
			method: 'GET',
			params: {
				therapistId: store.selectedTherapist.encrypted_id,
			},
		});

		store.setAvailableDays(data.value ?? []);
		loadingAvailableDays.value = false;
	};

	const fetchRemainingSlots = async () => {
		loadingRemainingSlots.value = true;

		const {
			data,
		} = await useFetch('/api/appointment-planner/v1/remaining-slots', {
			method: 'GET',
		});

		if (data.value) {
			remainingSlots.value = data.value;
		}

		loadingRemainingSlots.value = false;
	};

	const fetchAvailableTimeBlocks = async () => {
		if (!store.selectedTherapist || !store.selectedDay) {
			availableTimeBlocks.value = [];
			return;
		}

		loadingAvailableTimeBlocks.value = true;

		const {
			data,
		} = await useFetch('/api/appointment-planner/v1/available-time-blocks', {
			method: 'GET',
			params: {
				therapistId: store.selectedTherapist.encrypted_id,
				date: store.selectedDay.date,
			},
		});

		availableTimeBlocks.value = data.value ?? [];
		loadingAvailableTimeBlocks.value = false;
	};

	const setPatientData = async (patientData: NullablePatientDataType) => {
		store.setPatientData({
			...store.patientData,
			...patientData,
		});
	};

	const bookAppointment = async () => {
		if (!store.isPatientDataComplete || !store.isPatientDataValid || !store.isAppointmentDataComplete) {
			throw new Error('Appointment / patient data is not complete or valid');
		}

		const body: BookAppointmentDataType = {
			patient: store.patientData as PatientDataType,
			appointment: {
				additional_info: store.additionalInfo,
				date: dayjs(store.selectedDay?.date as string).format('YYYY-MM-DD'),
				encrypted_therapist_id: store.selectedTherapist?.encrypted_id as string,
				from: dayjs(store.selectedTimeBlock?.from as string).format('HH:mm'),
				till: dayjs(store.selectedTimeBlock?.till as string).format('HH:mm'),
			},
		};

		const {
			data, error,
		} = await useFetch('/api/appointment-planner/v1/book-appointment', {
			method: 'POST',
			body,
		});

		if (error.value || !data.value) {
			if (error.value?.data.data.errors && Object.values(error.value.data.data.errors).length > 0) {
				for (const errorArr of Object.values(error.value.data.data.errors)) {
					notifications.push({
						type: 'error',
						message: (errorArr as string[]).join(' '),
						title: 'Er is iets fout gegaan',
					});
				}

				throw new Error('Couldn\'t book appointment: ' + error.value.data.data.message);
			}
			else {
				notifications.push({
					type: 'error',
					message: 'Er is iets fout gegaan bij het boeken van de afspraak. Probeer het later opnieuw.',
					title: 'Er is iets fout gegaan',
				});

				throw new Error('Couldn\'t book appointment');
			}
		}

		trackBookTherapySession();

		store.setSubmittedAppointmentDate(`${data.value.date} ${data.value.time}`);
		store.setHasPlannedAppointment(true);
		store.setSubmitResult(data.value);
	};

	const resetData = () => {
		store.resetStoreData();
	};

	return {
		fetchTherapists,
		hasMultipleTherapists: computed(() => store.hasMultipleTherapists),
		loadingTherapists: computed(() => loadingTherapists.value),
		therapists: computed(() => store.therapists),
		selectTherapist,
		selectedTherapist: computed(() => store.selectedTherapist),

		fetchRemainingSlots,
		remainingSlots: computed(() => remainingSlots.value),

		fetchAvailableDays,
		loadingAvailableDays: computed(() => loadingAvailableDays.value),
		availableDays: computed(() => store.availableDays),
		selectDay,
		selectedDay: computed(() => store.selectedDay),
		hasAvailableWeeks: computed(() => store.hasAvailableWeeks),

		fetchAvailableTimeBlocks,
		loadingAvailableTimeBlocks: computed(() => loadingAvailableTimeBlocks.value),
		availableTimeBlocks: computed(() => availableTimeBlocks.value),
		selectTimeBlock,
		selectedTimeBlock: computed(() => store.selectedTimeBlock),

		setPatientData,
		setAdditionalInfo: store.setAdditionalInfo,
		patientData: computed(() => store.patientData),
		appointmentDataOverview: computed(() => store.appointmentDataOverview),
		patientDataOverview: computed(() => store.patientDataOverview),
		patientDataConfirmed: computed(() => store.patientDataConfirmed),
		setPatientDataConfirmed: store.setPatientDataConfirmed,
		isPatientDataComplete: computed(() => store.isPatientDataComplete),
		isPatientDataValid: computed(() => store.isPatientDataValid),
		bookAppointment,

		submitResult: computed(() => store.submitResult),
		hasPlannedAppointment: computed(() => store.hasPlannedAppointment),
		resetData,
		hasAppointmentInFuture: computed(() => {
			if (!store.submittedAppointmentDate) {
				return false;
			}

			return dayjs(store.submittedAppointmentDate) > dayjs();
		}),
	};
};
